<template>
  <div class="flex h-full flex-col bg-white dark:bg-gray-900 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200 overflow-hidden border border-gray-200 dark:border-gray-700">
    <!-- Card Header -->
    <div class="flex-1 p-4 sm:p-6">
      <div class="flex items-start justify-between">
        <div class="flex-1 min-w-0">
          <!-- Item Image and Name -->
          <div class="flex items-start gap-3">
            <div
              v-if="getImageUrl(item)"
              class="flex-shrink-0"
            >
              <img
                class="h-16 w-16 rounded-lg object-cover"
                :src="getImageUrl(item)"
                :alt="item.name"
              >
            </div>
            <div class="flex-1 min-w-0">
              <!-- Item Name -->
              <h3 class="text-lg font-medium text-gray-900 dark:text-gray-100 truncate">
                {{ item.name }}
              </h3>
              
              <!-- Item Description -->
              <p
                v-if="item.description"
                class="mt-1 text-sm text-gray-500 dark:text-gray-400 line-clamp-2"
              >
                {{ item.description }}
              </p>
              
              <!-- Category and Status Badges -->
              <div class="mt-2 flex items-center gap-2 flex-wrap">
                <span
                  v-if="getItemCategoryName(item)"
                  class="inline-flex items-center rounded-md bg-blue-50 dark:bg-blue-950 px-2 py-1 text-xs font-medium text-blue-700 dark:text-blue-100"
                >
                  {{ getItemCategoryName(item) }}
                </span>
                <MenuItemStatusBadge
                  :item="item"
                  variant="desktop"
                />
              </div>
            </div>
          </div>
        </div>

        <div class="ml-3 flex shrink-0 flex-col items-end gap-2">
          <!-- Actions Dropdown -->
          <div
            v-if="canManage"
            @click.stop
          >
            <DropdownMenu
              :id="`menu-item-card-${item.id}`"
              button-label="Menu item actions"
              width="md"
            >
              <DropdownMenuItem
                :icon="PencilIcon"
                variant="default"
                @click="$emit('edit', item)"
              >
                {{ $t('app.actions.edit') }}
              </DropdownMenuItem>

              <DropdownMenuItem
                :icon="item.is_available ? EyeSlashIcon : EyeIcon"
                variant="default"
                @click="$emit('toggleAvailability', item)"
              >
                {{ item.is_available ? $t('app.views.menu.list.disable') : $t('app.views.menu.list.enable') }}
              </DropdownMenuItem>

              <DropdownMenuDivider />

              <DropdownMenuItem
                :icon="TrashIcon"
                variant="danger"
                @click="$emit('delete', item)"
              >
                {{ $t('app.actions.delete') }}
              </DropdownMenuItem>
            </DropdownMenu>
          </div>

          <MenuItemPriceDisplay
            v-if="!hasVariants(item)"
            :item="item"
            variant="desktop"
          />
        </div>
      </div>
    </div>

    <div
      v-if="hasVariants(item)"
      class="px-4 sm:px-6 py-3 border-t border-gray-200 dark:border-gray-700"
    >
      <div class="flex items-center justify-between">
        <div class="flex-1">
          <MenuItemPriceDisplay
            :item="item"
            variant="desktop"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import DropdownMenu from '@/components/ui/DropdownMenu.vue';
import DropdownMenuItem from '@/components/ui/DropdownMenuItem.vue';
import DropdownMenuDivider from '@/components/ui/DropdownMenuDivider.vue';
import { PencilIcon, TrashIcon, EyeIcon, EyeSlashIcon } from '@heroicons/vue/24/outline';
import type { MenuItem } from '@/types/menu';
import MenuItemStatusBadge from './MenuItemStatusBadge.vue';
import MenuItemPriceDisplay from './MenuItemPriceDisplay.vue';

defineProps<{
  item: MenuItem;
  canManage: boolean;
}>();

defineEmits<{
  edit: [item: MenuItem];
  delete: [item: MenuItem];
  toggleAvailability: [item: MenuItem];
}>();

// Helper function to get image URL
function getImageUrl(item: MenuItem): string | undefined {
  return item.imageUrl || item.image_url;
}

// Helper function to get category name
function getItemCategoryName(item: MenuItem): string | null {
  if (!item.category) {
    return null;
  }
  return typeof item.category === 'string' ? item.category : item.category.name;
}

function hasVariants(item: MenuItem): boolean {
  return Boolean(item.variants && item.variants.length > 0);
}

// Format date helper
function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('es-MX', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).format(date);
}
</script>
